/* Optional velocity recommendations for the existing slide cart: a carousel
   above the subtotal while the cart has items, and a short "Popular right now"
   list in the empty-cart space when it has none. Both use the same feed. */
(() => {
  'use strict';

  document.addEventListener('DOMContentLoaded', () => {
    const slide = window.slideCart;
    const mount = document.querySelector('[data-slide-cart-recommendations]');
    const cards = mount?.querySelector('[data-slide-cart-recommendation-cards]');
    if (!slide || !mount || !cards) return;
    const nav = mount.querySelector('[data-slide-cart-recommendation-nav]');
    // Analytics: versioned feed label; views count once per page when the
    // carousel is actually on screen (the drawer is open).
    const FEED_LABEL = 'pairs@v1';
    const CART_SOURCE = 'native-cart-widget';
    const EMPTY_SOURCE = 'native-cart-empty-widget';
    function track(event, widget = 'cart') {
      const url = mount.dataset.eventsUrl;
      if (!url || !navigator.sendBeacon) return;
      try { navigator.sendBeacon(url, JSON.stringify({w: widget, f: FEED_LABEL, e: event})); } catch { /* never break the cart */ }
    }
    let viewed = false;
    let viewObserver = null;
    function watchView() {
      if (viewed || viewObserver || !('IntersectionObserver' in window)) return;
      viewObserver = new IntersectionObserver(entries => {
        if (!viewed && !mount.hidden && entries.some(entry => entry.isIntersecting)) {
          viewed = true;
          viewObserver.disconnect();
          track('view');
        }
      }, {threshold: 0.3});
      viewObserver.observe(mount);
    }
    document.addEventListener('slide-cart:added', event => {
      const props = event.detail?.properties;
      if (props && props._source === CART_SOURCE) track('add');
      if (props && props._source === EMPTY_SOURCE) track('add', 'empty');
    });
    const dots = mount.querySelector('[data-slide-cart-recommendation-dots]');
    const prevButton = mount.querySelector('[data-slide-cart-recommendation-prev]');
    const nextButton = mount.querySelector('[data-slide-cart-recommendation-next]');

    const decimalId = (value, type) => typeof value === 'string'
      ? new RegExp(`^gid://shopify/${type}/([1-9]\\d*)$`).exec(value)?.[1]
      : null;
    let cache;
    let activeRequest = null;
    let disposed = false;
    let suspended = false;
    let rendered = '';
    let selected = [];
    let index = 0;

    function isOpen() {
      return !slide.elements.slideCart?.classList.contains('translate-x-full');
    }

    function historyIds() {
      try {
        const snapshot = globalThis.purchaseLimitHistory?.readSnapshot(document);
        // Map of product id -> units bought in the window; only presence matters here.
        return snapshot?.status === 'ok' && snapshot.quantities instanceof Map ? snapshot.quantities : new Map();
      } catch { return new Map(); }
    }

    function hide() {
      mount.hidden = true;
      cards.replaceChildren();
      if (dots) { dots.replaceChildren(); dots.hidden = true; }
      if (nav) nav.hidden = true;
      rendered = '';
      selected = [];
      index = 0;
    }

    function safeImage(value) {
      if (typeof value !== 'string') return null;
      try {
        const url = new URL(value);
        return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
      } catch { return null; }
    }

    function formatPrice(amount, currency) {
      const locale = document.documentElement.lang || undefined;
      for (const currencyDisplay of ['narrowSymbol', 'symbol', 'code']) {
        try {
          return new Intl.NumberFormat(locale, {style: 'currency', currency, currencyDisplay}).format(amount);
        } catch { /* older engines reject narrowSymbol; fall through */ }
      }
      return `${currency} ${amount.toFixed(2)}`;
    }

    function validCard(rec) {
      if (!rec || typeof rec !== 'object' || rec.availableForSale !== true) return null;
      const productId = decimalId(rec.productId, 'Product');
      const variantId = decimalId(rec.variantId, 'ProductVariant');
      const image = safeImage(rec.imageUrl);
      const amount = rec.price?.amount;
      const currency = rec.price?.currencyCode;
      if (!productId || !variantId || !image || typeof rec.title !== 'string' || !rec.title.trim()) return null;
      if (typeof amount !== 'string' || !/^\d+(?:\.\d+)?$/.test(amount) || !Number.isFinite(Number(amount))) return null;
      if (typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency) || !Intl.supportedValuesOf('currency').includes(currency)) return null;
      const price = formatPrice(Number(amount), currency);
      const vendor = typeof rec.vendor === 'string' && rec.vendor.trim() ? rec.vendor.trim() : null;
      const handle = typeof rec.handle === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(rec.handle) ? rec.handle : null;
      return {productId, variantId, image, title: rec.title.trim(), price, vendor, handle, limitOne: rec.limitOne === true};
    }

    // One product card (image, name, price, Add). `source` tags the line so
    // orders and adds are attributed to the right widget.
    function cardNode(card, source) {
      const shell = document.createElement('div');
      shell.className = 'slide-cart-recs__card';
      const media = document.createElement(card.handle ? 'a' : 'div');
      media.className = 'slide-cart-recs__media';
      if (card.handle) { media.href = `/products/${card.handle}`; media.setAttribute('aria-label', card.title); }
      const img = document.createElement('img');
      img.src = card.image;
      img.alt = '';
      img.width = 120;
      img.height = 120;
      img.loading = 'lazy';
      img.className = 'slide-cart-recs__image';
      media.append(img);

      const text = document.createElement('div');
      text.className = 'slide-cart-recs__text';
      const title = document.createElement('p');
      title.className = 'slide-cart-recs__name';
      if (card.handle) {
        const link = document.createElement('a');
        link.href = `/products/${card.handle}`;
        link.className = 'slide-cart-recs__link';
        link.textContent = card.title;
        title.append(link);
      } else {
        title.textContent = card.title;
      }
      const price = document.createElement('p');
      price.className = 'slide-cart-recs__price';
      price.textContent = card.price;
      text.append(title, price);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'add-to-cart-btn slide-cart-recs__add';
      button.textContent = 'Add';
      button.setAttribute('aria-label', `Add ${card.title}`);
      Object.assign(button.dataset, {
        productId: card.productId,
        variantId: card.variantId,
        quantity: '1',
        properties: JSON.stringify({_source: source, _feed: FEED_LABEL})
      });
      if (card.limitOne) button.dataset.limit = '1';

      shell.append(media, text, button);
      return shell;
    }

    // Paints the card at `index` plus its dots. Selection lives in `selected`.
    function paint() {
      const card = selected[index];
      if (!card) { hide(); return; }

      cards.replaceChildren(cardNode(card, CART_SOURCE));

      const many = selected.length > 1;
      if (nav) nav.hidden = !many;
      if (dots) {
        if (many) {
          const fragment = document.createDocumentFragment();
          selected.forEach((_, position) => {
            const dot = document.createElement('span');
            dot.className = position === index ? 'slide-cart-recs__dot slide-cart-recs__dot--on' : 'slide-cart-recs__dot';
            fragment.append(dot);
          });
          dots.replaceChildren(fragment);
          dots.hidden = false;
        } else {
          dots.replaceChildren();
          dots.hidden = true;
        }
      }
      mount.hidden = false;
      watchView();
    }

    // Empty cart: up to three stacked cards under "Your cart is empty". The cart
    // re-renders its item list on every update, so this re-attaches as needed.
    const EMPTY_PICKS = 3;
    // Each open of an empty cart shows the next three from the same feed. The
    // turn is kept in localStorage so it keeps moving across page loads.
    const EMPTY_TURN_KEY = 'slideCartEmptyTurn';
    let emptyTurn = 0;
    function nextEmptyTurn() {
      let turn = 0;
      try { turn = Number.parseInt(localStorage.getItem(EMPTY_TURN_KEY), 10) || 0; } catch { /* storage blocked */ }
      try { localStorage.setItem(EMPTY_TURN_KEY, String((turn + 1) % 1000)); } catch { /* storage blocked */ }
      return turn;
    }
    let emptyRendered = '';
    let emptyViewed = false;
    let emptyObserver = null;
    const hasItems = () => slide.cart.items.some(item => item.quantity > 0);

    function clearEmpty() {
      slide.elements.cartItems?.querySelector('[data-slide-cart-empty-recs]')?.remove();
      emptyRendered = '';
    }

    function watchEmptyView(section) {
      if (emptyViewed || !('IntersectionObserver' in window)) return;
      emptyObserver?.disconnect();
      emptyObserver = new IntersectionObserver(entries => {
        if (!emptyViewed && entries.some(entry => entry.isIntersecting)) {
          emptyViewed = true;
          emptyObserver.disconnect();
          track('view', 'empty');
        }
      }, {threshold: 0.3});
      emptyObserver.observe(section);
    }

    function renderEmpty() {
      const host = slide.elements.cartItems;
      if (!host || disposed || hasItems() || !cache) { clearEmpty(); return; }
      const purchased = historyIds();
      const seen = new Set();
      const eligible = [];
      for (const rec of cache) {
        const card = validCard(rec);
        if (!card || seen.has(card.productId) || (card.limitOne && purchased.has(card.productId))) continue;
        eligible.push(card);
        seen.add(card.productId);
      }
      const start = eligible.length ? (emptyTurn * EMPTY_PICKS) % eligible.length : 0;
      const picked = eligible.length <= EMPTY_PICKS
        ? eligible
        : Array.from({length: EMPTY_PICKS}, (_, offset) => eligible[(start + offset) % eligible.length]);
      if (!picked.length) { clearEmpty(); return; }
      const next = JSON.stringify(picked);
      const existing = host.querySelector('[data-slide-cart-empty-recs]');
      if (existing && next === emptyRendered) return;

      const section = document.createElement('section');
      section.className = 'slide-cart-empty-recs';
      section.setAttribute('data-slide-cart-empty-recs', '');
      const head = document.createElement('h3');
      head.className = 'slide-cart-recs__head';
      head.textContent = 'Popular right now';
      section.append(head, ...picked.map(card => cardNode(card, EMPTY_SOURCE)));
      if (existing) existing.replaceWith(section); else host.append(section);
      emptyRendered = next;
      watchEmptyView(section);
    }

    function step(delta) {
      if (mount.hidden || selected.length < 2) return;
      index = (index + delta + selected.length) % selected.length;
      try { paint(); } catch { hide(); return; }
      // Subtle enter animation on the freshly painted card (CSS handles
      // prefers-reduced-motion). Class is added after paint so it animates in.
      const shell = cards.firstElementChild;
      if (shell) {
        shell.classList.add(delta > 0 ? 'slide-cart-recs__card--from-right' : 'slide-cart-recs__card--from-left');
        requestAnimationFrame(() => requestAnimationFrame(() => shell.classList.add('slide-cart-recs__card--settled')));
      }
    }

    prevButton?.addEventListener('click', () => step(-1));
    nextButton?.addEventListener('click', () => step(1));

    // Horizontal swipe on touch devices. A swipe of 40px or more with a mostly
    // horizontal direction moves one card; anything else is left to scrolling.
    let swipeStart = null;
    cards.addEventListener('pointerdown', event => {
      if (event.pointerType === 'mouse') return;
      swipeStart = {x: event.clientX, y: event.clientY};
    });
    cards.addEventListener('pointerup', event => {
      if (!swipeStart) return;
      const dx = event.clientX - swipeStart.x;
      const dy = event.clientY - swipeStart.y;
      swipeStart = null;
      if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
    });
    cards.addEventListener('pointercancel', () => { swipeStart = null; });

    function render() {
      if (!hasItems()) { hide(); try { renderEmpty(); } catch { clearEmpty(); } return; }
      if (disposed || !isOpen() || !cache) { hide(); return; }
      const excluded = new Set(slide.cart.items.filter(item => item.quantity > 0).map(item => String(item.product_id)));
      const purchased = historyIds();
      const picked = [];
      for (const rec of cache) {
        const card = validCard(rec);
        if (!card || excluded.has(card.productId) || (card.limitOne && purchased.has(card.productId))) continue;
        picked.push(card);
        excluded.add(card.productId);
        if (picked.length === 3) break;
      }
      if (!picked.length) { hide(); return; }
      const next = JSON.stringify(picked);
      if (next === rendered) return;

      selected = picked;
      // Keep the shopper's place across cart updates; only reset when it no longer exists.
      if (index >= selected.length) index = 0;
      paint();
      rendered = next;
    }

    function safelyRender() {
      try { render(); } catch { try { hide(); } catch { mount.hidden = true; } }
    }

    function request() {
      const attempt = {};
      activeRequest = attempt;
      const controller = new AbortController();
      const deadline = performance.now() + 3000;
      let expired = false;
      let timer;
      const timeout = new Promise(resolve => {
        attempt.cancel = () => { expired = true; clearTimeout(timer); controller.abort(); resolve(null); };
        timer = setTimeout(attempt.cancel, 3000);
      });
      const read = async () => {
        const response = await fetch(mount.dataset.recommendationsUrl, {
          signal: controller.signal,
          credentials: 'omit',
          headers: {Accept: 'application/json'}
        });
        if (!response.ok) return null;
        const payload = await response.json();
        return Array.isArray(payload?.recommendations) ? payload.recommendations : null;
      };
      Promise.race([read(), timeout]).then(list => {
        if (activeRequest !== attempt) return;
        if (!disposed && !expired && performance.now() < deadline && list !== null) cache = list;
        safelyRender();
      }).catch(() => { if (activeRequest === attempt) safelyRender(); }).finally(() => {
        clearTimeout(timer);
        if (activeRequest === attempt) activeRequest = null;
      });
    }

    function update(event) {
      if (event.type === 'slide-cart:opened') { try { if (!hasItems()) emptyTurn = nextEmptyTurn(); } catch { /* keep last turn */ } }
      safelyRender();
      if (!disposed && isOpen()
        && cache === undefined && !activeRequest && (event.type === 'slide-cart:opened' || event.type === 'slide-cart:updated')) request();
    }

    document.addEventListener('slide-cart:opened', update);
    document.addEventListener('slide-cart:updated', update);
    window.addEventListener('pagehide', event => {
      disposed = true;
      suspended = event.persisted === true;
      const attempt = activeRequest;
      activeRequest = null;
      attempt?.cancel();
      safelyRender();
    });
    window.addEventListener('pageshow', event => {
      if (!event.persisted || !suspended) return;
      disposed = false;
      suspended = false;
      safelyRender();
    });
  });
})();
